import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import axios from 'axios';

const FolderContext = createContext();

const RUN_DATE_LOOKBACK_MONTHS = 3;

/** Keep folders whose runDate (YYYYMMDD) is within the last N months. */
const filterRecentRunDateFolders = (folderList, months = RUN_DATE_LOOKBACK_MONTHS) => {
  const cutoff = new Date();
  cutoff.setHours(0, 0, 0, 0);
  cutoff.setMonth(cutoff.getMonth() - months);
  return (folderList || []).filter((folder) => {
    const s = String(folder?.runDate || '');
    if (s.length !== 8) return false;
    const d = new Date(`${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`);
    return !isNaN(d.getTime()) && d >= cutoff;
  });
};

export const useFolder = () => {
  const context = useContext(FolderContext);
  if (!context) {
    throw new Error('useFolder must be used within FolderProvider');
  }
  return context;
};

export const FolderProvider = ({ children }) => {
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [selectedRunDate, setSelectedRunDate] = useState(null);
  const [selectedModelDate, setSelectedModelDate] = useState(null);
  const [folderInfo, setFolderInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [debugMode, setDebugMode] = useState(false);

  // Group folders by run date
  const foldersByRunDate = useMemo(() => {
    const grouped = {};
    folders.forEach(folder => {
      const runDate = folder.runDate;
      if (!grouped[runDate]) {
        grouped[runDate] = [];
      }
      grouped[runDate].push(folder);
    });
    // Sort model dates within each run date (ascending - oldest first)
    Object.keys(grouped).forEach(runDate => {
      grouped[runDate].sort((a, b) => a.modelDate.localeCompare(b.modelDate));
    });
    return grouped;
  }, [folders]);

  // Get unique run dates sorted (latest first)
  const runDates = useMemo(() => {
    return Object.keys(foldersByRunDate).sort().reverse();
  }, [foldersByRunDate]);

  const getLatestFolderForRunDate = (runDate, grouped = foldersByRunDate) => {
    const list = grouped[runDate];
    if (!list || list.length === 0) return null;
    return list[list.length - 1];
  };

  // Load available folders
  useEffect(() => {
    loadFolders();
  }, []);

  // Load current folder info when selected
  useEffect(() => {
    if (selectedFolder) {
      loadFolderInfo();
    }
  }, [selectedFolder]);

  const loadFolders = async () => {
    try {
      const response = await axios.get('/api/folders');
      const recentFolders = filterRecentRunDateFolders(response.data);
      setFolders(recentFolders);
      
      // Default to All for the latest run date
      if (recentFolders.length > 0 && !selectedFolder) {
        const grouped = {};
        recentFolders.forEach(folder => {
          const runDate = folder.runDate;
          if (!grouped[runDate]) {
            grouped[runDate] = [];
          }
          grouped[runDate].push(folder);
        });

        Object.keys(grouped).forEach(runDate => {
          grouped[runDate].sort((a, b) => a.modelDate.localeCompare(b.modelDate));
        });

        const sortedRunDates = Object.keys(grouped).sort().reverse();
        if (sortedRunDates.length > 0) {
          const latestRunDate = sortedRunDates[0];
          setSelectedRunDate(latestRunDate);
          setSelectedModelDate('ALL');
          setSelectedFolder(null);
          setFolderInfo(null);
        }
      }
    } catch (error) {
      console.error('Error loading folders:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadFolderInfo = async () => {
    try {
      const response = await axios.post('/api/load-folder', {
        folderName: selectedFolder
      });
      setFolderInfo({
        publishDate: response.data.publishDate,
        forecastWeek: response.data.forecastWeek
      });
    } catch (error) {
      console.error('Error loading folder info:', error);
    }
  };

  const handleRunDateChange = async (runDate) => {
    if (runDate === selectedRunDate) {
      return;
    }

    setSelectedRunDate(runDate);
    setSelectedModelDate('ALL');
    setSelectedFolder(null);
    setFolderInfo(null);
    setRefreshing(false);
  };

  const handleModelDateChange = async (modelDate) => {
    if (!modelDate) {
      return;
    }
    if (modelDate === selectedModelDate) {
      return;
    }

    setSelectedModelDate(modelDate);

    if (modelDate === 'ALL') {
      setSelectedFolder(null);
      setFolderInfo(null);
      setRefreshing(false);
      return;
    }

    if (!selectedRunDate) return;
    const folder = foldersByRunDate[selectedRunDate]?.find(f => f.modelDate === modelDate);
    if (folder) {
      handleFolderChangeByName(folder.name);
    }
  };

  const handleFolderChangeByName = async (folderName) => {
    if (folderName === selectedFolder) {
      return; // No change needed
    }
    
    // Set refreshing state and clear existing data
    setRefreshing(true);
    setFolderInfo(null); // Clear folder info
    
    // Update selected folder
    setSelectedFolder(folderName);
    
    // Load new folder data
    try {
      const response = await axios.post('/api/load-folder', {
        folderName: folderName
      });
      setFolderInfo({
        publishDate: response.data.publishDate,
        forecastWeek: response.data.forecastWeek
      });
    } catch (error) {
      console.error('Error loading folder info:', error);
    } finally {
      // Add a small delay to ensure data is loaded
      setTimeout(() => {
        setRefreshing(false);
      }, 500);
    }
  };

  // Legacy function for backward compatibility
  const handleFolderChange = async (folderName) => {
    // Find folder to extract run date and model date
    const folder = folders.find(f => f.name === folderName);
    if (folder) {
      setSelectedRunDate(folder.runDate);
      setSelectedModelDate(folder.modelDate);
      await handleFolderChangeByName(folderName);
    }
  };

  return (
    <FolderContext.Provider
      value={{
        folders,
        foldersByRunDate,
        runDates,
        selectedFolder,
        selectedRunDate,
        selectedModelDate,
        folderInfo,
        loading,
        refreshing,
        handleFolderChange,
        handleRunDateChange,
        handleModelDateChange,
        debugMode,
        setDebugMode
      }}
    >
      {children}
    </FolderContext.Provider>
  );
};

